import { StandardEvents } from '@shopify/events';

// The existing product-form-component owns requests and cart/drawer updates.
// This element only validates its controls and observes the native result promise.
class OogaCardPurchase extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.formComponent = this.querySelector('product-form-component');
    this.form = this.querySelector('form');
    this.input = this.querySelector('[name="quantity"]');
    this.button = this.querySelector('[type="submit"]');
    this.error = this.querySelector('.ooga-card-purchase__error');
    this.live = this.querySelector('[ref="liveRegion"]');
    if (!this.form) return;
    this.busy = false;
    this.addEventListener('click', event => {
      // Prevent the enclosing product card from treating form actions as navigation.
      if (event.target.closest('button, input')) event.stopPropagation();
      const step = event.target.closest('[data-step]');
      if (!step || step.disabled) return;
      this.input.setCustomValidity('');
      if (!Number.isFinite(this.input.valueAsNumber)) this.input.value = this.dataset.minimum;
      if (Number(step.dataset.step) > 0) this.input.stepUp();
      else this.input.stepDown();
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }, { signal });
    this.input.addEventListener('input', () => {
      this.input.setCustomValidity('');
      this.syncControls();
    }, { signal });
    this.input.addEventListener('change', () => this.syncControls(), { signal });
    this.form.addEventListener('submit', event => {
      // Keep native POST fallback when the theme's module has not loaded.
      if (typeof this.formComponent.handleSubmit !== 'function') return;
      event.preventDefault();
      event.stopPropagation();
      if (this.busy || this.dataset.unavailable === 'true') return;
      this.input.setCustomValidity('');
      this.syncControls();
      if (!this.form.reportValidity()) return;
      this.clearFeedback();
      this.busy = true;
      this.syncControls();
      this.live.textContent = this.dataset.addingMessage;
      try {
        this.formComponent.handleSubmit(event);
      } catch {
        this.finishError(this.dataset.errorMessage);
      }
    }, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, event => this.onCartUpdate(event), { signal });
    this.formComponent.addEventListener(StandardEvents.cartError, event => {
      // Native errors include network failures; never show success from a click.
      const message = event.detail?.description || event.error || this.dataset.errorMessage;
      this.showError(message);
    }, { signal });
    this.syncControls();
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.resetTimer);
  }

  syncControls() {
    if (!this.input) return;
    const minimum = Number(this.dataset.minimum) || 1;
    const increment = Number(this.dataset.increment) || 1;
    const maximum = this.dataset.maximum === '' ? Infinity : Number(this.dataset.maximum);
    const remaining = maximum - (Number(this.dataset.cartQuantity) || 0);
    if (Number.isFinite(remaining)) this.input.max = String(Math.max(0, remaining));
    else this.input.removeAttribute('max');
    const unavailable = this.dataset.unavailable === 'true';
    const quantity = this.input.valueAsNumber;
    this.querySelector('[data-step="-1"]').disabled = this.busy || unavailable || !(quantity > minimum);
    this.querySelector('[data-step="1"]').disabled = this.busy || unavailable || quantity + increment > remaining;
    this.input.readOnly = this.busy;
    this.button.disabled = this.busy || unavailable || remaining < minimum;
    this.button.setAttribute('aria-busy', String(this.busy));
    if (!unavailable && remaining < minimum && !this.busy && !this.button.classList.contains('is-added')) {
      this.showError(this.dataset.quantityMessage);
    }
  }

  async onCartUpdate(event) {
    if (!event.promise) return;
    const ownOperation = event.target === this.formComponent && event.action === 'add';
    try {
      const result = await event.promise;
      if (!this.isConnected) return;
      // Sum all lines for this variant, including lines split by properties/plans.
      if (Array.isArray(result.detail?.items)) {
        this.dataset.cartQuantity = String(result.detail.items.reduce((total, item) =>
          total + (String(item.variant_id ?? item.id) === this.dataset.variantId ? Number(item.quantity) || 0 : 0), 0));
      }
      if (!ownOperation) { this.syncControls(); return; }
      const errors = result.userErrors || [];
      const warnings = result.warnings || [];
      if (result.detail?.didError || errors.length || warnings.length) {
        this.finishError(errors[0]?.message || warnings[0]?.message || this.error.textContent || this.dataset.errorMessage);
        return;
      }
      this.busy = false;
      this.error.hidden = true;
      this.error.textContent = '';
      this.button.classList.add('is-added');
      this.live.textContent = this.dataset.addedMessage;
      this.syncControls();
      clearTimeout(this.resetTimer);
      this.resetTimer = setTimeout(() => {
        this.button.classList.remove('is-added');
        this.live.textContent = '';
        this.syncControls();
      }, 1600);
    } catch {
      if (ownOperation && this.isConnected) this.finishError(this.dataset.errorMessage);
    }
  }

  clearFeedback() {
    clearTimeout(this.resetTimer);
    this.button.classList.remove('is-added');
    this.error.hidden = true;
    this.error.textContent = '';
    this.live.textContent = '';
  }

  showError(message) {
    this.button.classList.remove('is-added');
    this.error.textContent = message;
    this.error.hidden = false;
  }

  finishError(message) {
    this.busy = false;
    this.live.textContent = '';
    this.syncControls();
    this.showError(message);
  }
}

if (!customElements.get('ooga-card-purchase')) customElements.define('ooga-card-purchase', OogaCardPurchase);
